import { useGetEnrollmentMultiGetStartedData, useSubmitEnrollmentMultiGetStartedData } from '@egw/api'
import { Alert, AlertType, Button, Checkbox, GenericError, Input, Link, ProgressBar } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES, type FeatureFlags } from '@egw/constants'
import { setContext } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { screenRecordingService } from '@egw/screen-recording'
import { useEffect, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { PageHeader } from '../../../components'
import { ApexDisclosures } from '../../../components/get-started-enrollment-disclosures/apex-disclosures'
import { EnrollmentPageLoading } from '../../../components/page-loading'
import { type Config } from '../../../config/config'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { getNameValidationRule } from '../../../utils/form-validation'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import {
  formatPhoneNumber,
  formatPhoneNumberWwithMask,
  preformatPhoneNumber,
} from '../../../utils/phone-number/phone-number'
import { getTestId, HtmlTestTypes } from '../../../utils/testing/get-test-id'
import { getZendeskAPIValue } from '../../../utils/zendesk/get-zendesk-api-value'

const log = new Logger('Enrollment - Multi - GetStarted')

interface FormData {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
}

export const GetStartedPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const sessionRecordingUrl = screenRecordingService.getSessionUrl()
  const { flags } = useFlags<FeatureFlags>()
  const [applicationIdSet, setApplicationIdSet] = useState(false)

  const config = configClient.getConfig() as Config

  useEffect(() => {
    // sendEvent(/* TODO: ADD EVENT FOR LANDING ON PAGE */)
  }, [])

  const { data, isLoading, error } = useGetEnrollmentMultiGetStartedData({
    enrollmentMetadata: {
      sessionRecordingUrl,
    },
  })

  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiGetStartedData()

  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.progress : undefined
  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  useEffect(() => {
    if (applicationId) {
      if (!applicationIdSet) {
        // Set userId through context update
        setContext({ userId: applicationId })
        setApplicationIdSet(true)
      }
    }
  }, [applicationId, applicationIdSet])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors: formErrors, isValid, touchedFields },
  } = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      agreements: undefined,
    },
    mode: 'onChange',
  })

  // Update form values when data is loaded
  useEffect(() => {
    if (data?.enrollmentData) {
      reset({
        email: data.enrollmentData.email || '',
        firstName: data.enrollmentData.firstName || '',
        lastName: data.enrollmentData.lastName || '',
        phoneNumber: formatPhoneNumber(data.enrollmentData.phoneNumber),
        agreements: undefined,
      })
    }
  }, [data, reset])

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: preformatPhoneNumber(formData.phoneNumber),
          privacyPolicy: {
            id: getZendeskAPIValue(config.zendeskArticles.privacyPolicy),
            accepted: true,
          },
          eSignConsent: {
            id: getZendeskAPIValue(config.zendeskArticles.eSignature),
            accepted: true,
          },
          triggerEmailOtp: true,
          phoneCountryCode: '1',
          enrollmentMetadata: {
            sessionRecordingUrl,
          },
        })
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  // Determine which description to use based on feature flag
  const description1Key = flags.rolloutJointEnrollmentV1
    ? 'multi.get_started.description_1_joint_enrollment'
    : 'multi.get_started.description_1'

  return (
    <EnrollmentMultiLayout title={t('multi.get_started.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? <EnrollmentPageLoading testId="get-started-loading" /> : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
          <PageHeader title={t('multi.get_started.title')} />
          <ProgressBar className="mb-md" progressPercentage={progress} showAnimation testId="enrollment_progress_bar" />
          <p className="typo-primary-base text-text-meta mb-md" data-test-id="description_1">
            {t(description1Key)}
          </p>
          <div className="flex flex-col md:flex-row md:gap-md">
            <Controller
              name="firstName"
              control={control}
              rules={{
                required: t('common.required_field'),
                ...getNameValidationRule(t('multi.get_started.first_name_error')),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('multi.get_started.first_name_label')}
                  required
                  error={touchedFields.firstName ? formErrors.firstName?.message : undefined}
                  type="text"
                  className="mb-sm w-full"
                  testId={getTestId('firstName', HtmlTestTypes.input)}
                  isPII
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{
                required: t('common.required_field'),
                ...getNameValidationRule(t('multi.get_started.last_name_error')),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('multi.get_started.last_name_label')}
                  required
                  error={touchedFields.lastName ? formErrors.lastName?.message : undefined}
                  type="text"
                  className="mb-sm w-full"
                  testId={getTestId('lastName', HtmlTestTypes.input)}
                  isPII
                />
              )}
            />
          </div>

          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              required: t('common.required_field'),
              pattern: {
                value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/i,
                message: t(`multi.get_started.invalid_phone_number`),
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t(`multi.get_started.phone_number`)}
                onInput={(e) => {
                  formatPhoneNumberWwithMask(e)
                }}
                value={formatPhoneNumber(field.value)}
                required
                testId="phoneNumber_input"
                error={touchedFields.phoneNumber ? formErrors.phoneNumber?.message : undefined}
                className="mb-sm"
                isPII
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: t('common.required_field'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]/i,
                message: t('common.invalid_email'),
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('multi.get_started.email_label')}
                required
                error={touchedFields.email ? formErrors.email?.message : undefined}
                autoComplete="email"
                type="email"
                className="mb-xl"
                testId={getTestId('email', HtmlTestTypes.input)}
                isPII
              />
            )}
          />

          <Controller
            name="agreements"
            control={control}
            rules={{
              required: t('common.required_field'),
            }}
            render={({ field }) => (
              <Checkbox
                {...field}
                required
                type="checkbox"
                className="mb-xl"
                testId={getTestId('agreements', HtmlTestTypes.checkbox)}
              >
                <span className="typo-primary-base mt-[2px]">
                  I agree to the{' '}
                  <Link className="typo-primary-base" href={config.zendeskArticles.privacyPolicy}>
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link className="typo-primary-base" href={config.zendeskArticles.eSignature}>
                    Electronic Signature Consent
                  </Link>
                  .
                </span>
              </Checkbox>
            )}
          />

          <p className="typo-primary-base text-text-meta md:mb-xl mb-4xl">
            {t('multi.get_started.phone_cta')}
            <Link
              className="typo-primary-base"
              href={`tel:${t('multi.get_started.phone_cta_number').replaceAll(' ', '')}`}
            >
              {t('multi.get_started.phone_cta_number')}
            </Link>
            .
          </p>

          <div className="hidden md:block">
            <Button
              type="submit"
              className="w-full md:w-min mb-5xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </div>

          <div className="block">
            <ApexDisclosures />
          </div>

          <div className="flex-1 md:hidden" />

          {submitError ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}

          {/* AUTO GENERATED COMPONENTS */}
        </form>
      ) : null}

      <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center px-lg py-xl bg-white shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.05)] md:hidden">
        <Button
          type="submit"
          className="w-full md:w-min md:mb-5xl"
          isDisabled={!isValid}
          isLoading={isSubmitting}
          testId="primaryAction_button"
          onClick={() => {
            if (isValid) {
              void onSubmit()
            }
          }}
        >
          {t('common.continue')}
        </Button>
      </div>

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
